import { useState, type ReactNode } from 'react'

import { Sheet, SheetContent, SheetHeader, SheetTrigger } from '@/components/ui'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { useIsMobile } from '@/hooks'
import { cn } from '@/infra'

export interface SelectOption<TId extends string> {
  id: TId
  label: string
}

interface SelectPopoverProps<TId extends string> {
  title: string
  options: ReadonlyArray<SelectOption<TId>>
  onSelect: (optionId: TId) => void
  triggerButton: ReactNode
  className?: string
  width?: string
  showIndicator?: boolean
}

export default function SelectPopover<TId extends string>({
  title,
  options,
  onSelect,
  triggerButton,
  className,
}: Readonly<SelectPopoverProps<TId>>) {
  const [open, setOpen] = useState(false)

  const handleSelect = (optionId: TId) => {
    onSelect(optionId)
    setOpen(false)
  }
  const isMobile = useIsMobile()
  const Container = isMobile ? Sheet : Popover
  const Trigger = isMobile ? SheetTrigger : PopoverTrigger
  const Content = isMobile ? SheetContent : PopoverContent

  return (
    <div className="relative">
      <Container open={open} onOpenChange={setOpen}>
        <Trigger asChild>{triggerButton}</Trigger>
        <div
          className={`${open ? 'inset-0 z-60 bg-black/30 md:fixed' : 'hidden'}`}
          data-slot="popover-overlay"
          data-testid="popover-overlay"
        >
          <Content
            className={cn(
              'md:width-auto rounded-t-xl md:relative md:z-100 md:mt-2 md:rounded-lg md:p-0 md:shadow-md',
              'max-h-[80vh] md:max-h-100',
              'gap-2 overflow-auto',
              className,
            )}
            align="center"
            sideOffset={5}
            side="bottom"
          >
            {isMobile ? (
              <SheetHeader>
                <div className="font-poppins relative z-20 text-left font-medium md:px-3">{title}</div>
              </SheetHeader>
            ) : (
              <div className="font-poppins relative z-20 my-2 text-left font-medium md:px-3">{title}</div>
            )}
            <div className="relative z-20 flex flex-col pb-2">
              {options.map((option) => (
                <div
                  role="none"
                  key={option.id}
                  className="font-nunito flex h-11 cursor-pointer items-center px-2 text-sm font-medium hover:bg-gray-100 md:h-14 md:px-6 md:font-normal"
                  onClick={() => handleSelect(option.id)}
                >
                  {option.label}
                </div>
              ))}
            </div>
          </Content>
        </div>
      </Container>
    </div>
  )
}
